import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  sitesSerivces,
  bâtimentsService,
  niveauxService,
  espacesService,
  piecesService,
} from "../../services";
import { showToast, Loading, CustomDialog } from "../../components";
import { Parcelle } from "../../interfaces";
import { AlerteArborescenceSite } from "./Alerts";
import EntityForm from "./EntityForm";
import { MultiSelect } from "primereact/multiselect";
import { useQuery, useQueryClient } from "@tanstack/react-query";

interface ModalEditSiteProps {
  uuid: string;
  parcelles: Parcelle[];
  modalIsOpenEditSite: boolean;
  closeModalEditSite: () => void;
}

type DeleteEntityType = "batiment" | "niveau" | "espace" | "piece";

interface ConfirmDeleteState {
  type: DeleteEntityType;
  id: number;
  label: string;
}

const ModalEditSite = ({
  uuid,
  parcelles,
  modalIsOpenEditSite,
  closeModalEditSite,
}: ModalEditSiteProps) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedEntityType, setSelectedEntityType] = useState<string>("");
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [parcelleError, setParcelleError] = useState<boolean>(false);
  const [confirmDelete, setConfirmDelete] = useState<ConfirmDeleteState | null>(null);

  const [modals, setModals] = useState({
    bâtiment: false,
    niveau: false,
    espace: false,
    pièce: false,
  });

  const { register, handleSubmit, setValue } = useForm();

  const { data: refSite } = useQuery({
    queryKey: ["site", uuid, selectedEntityType],
    queryFn: async () => await sitesSerivces.getByUuid(uuid),
    enabled: !!uuid,
  });



const [availableNiveaux, setAvailableNiveaux] = useState([]);


const openModal = (modalName: string, entity: any) => {
  setModals({ ...modals, [modalName]: true });
  setSelectedEntityType(modalName);

  if (modalName === "espace") {
    const niveaux = refSite?.batiments?.flatMap((b) => b.niveaux ?? []) ?? [];
    setAvailableNiveaux(niveaux);

    // Trouver le niveau parent et injecter son id dans l'entité
    const niveauParent = niveaux.find((n) =>
      n.espaces?.some((e) => e.id === entity?.id)
    );

    setSelectedEntity({
      ...entity,
      niveau_id: niveauParent?.id ?? null,
    });
  } else {
    setSelectedEntity(entity);
  }
};



  const closeModal = (modalName) => {
    setModals({ ...modals, [modalName]: false });
    setSelectedEntityType("");
    setSelectedEntity(null);
  };

  useEffect(() => {
    const mappedOptions = parcelles?.map((parcelle) => ({
      value: parcelle.id,
      label: parcelle.numéro,
    }));
    queryClient.setQueryData(["parcelles"], mappedOptions);
  }, [parcelles, queryClient]);

  useEffect(() => {
    if (refSite) {
      Object.keys(refSite).forEach((key) => {
        setValue(key, refSite[key]);
      });
      const refSiteParcelles = refSite.parcelles;
      if (Array.isArray(refSiteParcelles) && refSiteParcelles.length > 0) {
        const initialSelectedOptions = refSiteParcelles?.map(
          (parcelle) => parcelle.id
        );
        setSelectedOptions(initialSelectedOptions);
      }
    }
  }, [refSite, setValue]);

  useEffect(() => {
    if (selectedEntity) {
      Object.keys(selectedEntity).forEach((key) => {
        if (refSite && refSite[key] !== undefined) {
          setValue(key, refSite[key]);
        }
      });
    }
  }, [selectedEntity, refSite, setValue]);

  const handleSelectChange = (e) => {
    setSelectedOptions(e.value);
    setParcelleError(false);
    if (e.value.length === 0) {
      setParcelleError(true);
    }
  };

  // ─── Suppression avec confirmation ───────────────────────────────────────

  const deleteBatiment = async (id_batiment: number) => {
    try {
      setLoading(true);
      await bâtimentsService.delete(id_batiment);
      showToast("info", "Bâtiment supprimé");
      queryClient.refetchQueries({ queryKey: ["sites"] });
    } catch (e) {
      console.log("Error while fetching", e);
    } finally {
      setLoading(false);
    }
  };

  const deleteNiveau = async (id_niveau: number) => {
    try {
      setLoading(true);
      await niveauxService.delete(id_niveau);
      showToast("info", "Niveau supprimé");
      queryClient.refetchQueries({ queryKey: ["sites"] });
    } catch (e) {
      console.log("Error while fetching", e);
    } finally {
      setLoading(false);
    }
  };

  const deleteEspace = async (id_espace: number) => {
    try {
      setLoading(true);
      await espacesService.delete(id_espace);
      showToast("info", "Espace supprimé");
      queryClient.refetchQueries({ queryKey: ["sites"] });
    } catch (e) {
      console.log("Error while fetching", e);
    } finally {
      setLoading(false);
    }
  };

  const deletePiece = async (id_piece: number) => {
    try {
      setLoading(true);
      await piecesService.delete(id_piece);
      showToast("info", "Pièce supprimée");
      queryClient.refetchQueries({ queryKey: ["sites"] });
    } catch (e) {
      console.log("Error while fetching", e);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    const { type, id } = confirmDelete;
    setConfirmDelete(null);

    const actions: Record<DeleteEntityType, () => Promise<void>> = {
      batiment: () => deleteBatiment(id),
      niveau:   () => deleteNiveau(id),
      espace:   () => deleteEspace(id),
      piece:    () => deletePiece(id),
    };

    await actions[type]();
  };

  // ─── Labels lisibles selon le type ───────────────────────────────────────

  const deleteTypeLabels: Record<DeleteEntityType, string> = {
    batiment: "bâtiment",
    niveau:   "niveau",
    espace:   "espace",
    piece:    "pièce",
  };

  // ─── Soumission du formulaire site ───────────────────────────────────────

  const onSubmit = async (data: any) => {
    if (selectedOptions.length === 0) {
      setParcelleError(true);
      return;
    }

    try {
      const params = {
        nom_site: data.nom_site,
        parcelles: selectedOptions,
      };
      setLoading(true);
      await sitesSerivces.update(data.id, params);
      showToast("success", "Site modifié avec succès");
      queryClient.refetchQueries({ queryKey: ["sites"] });
      setSelectedOptions([]);
      setParcelleError(false);
      closeModalEditSite();
    } catch (e) {
      console.log("Error while fetching", e);
    } finally {
      setLoading(false);
    }
  };

  // ─── Rendu ───────────────────────────────────────────────────────────────

  return (
    <>
      {/* ── Dialog principal : modification du site ── */}
      <CustomDialog
        visible={modalIsOpenEditSite}
        closeModalDialog={() => {
          closeModalEditSite();
          setSelectedOptions([]);
          setParcelleError(false);
        }}
        header="Modifier site"
      >
        <div className="relative w-full h-full">
          <div className="p-6 space-y-6">
            <form
              onSubmit={handleSubmit(onSubmit)}
              encType="multipart/form-data"
            >
              <div className="grid grid-cols-6 gap-6">
                {/* Nom du site */}
                <div className="col-span-6 sm:col-span-6">
                  <label
                    htmlFor="nom_site"
                    className="block text-md font-bold leading-6 text-gray-900"
                  >
                    Nom
                  </label>
                  <input
                    type="text"
                    name="nom_site"
                    id="nom_site"
                    placeholder="Saisir votre nom de site"
                    className="my-3 border border-gray-300 text-gray-900 font-normal text-md rounded-lg focus:ring-gexpertise-100 focus:border-gexpertise-200 block w-full p-2.5"
                    {...register("nom_site")}
                  />
                </div>

                {/* Parcelles */}
                <div className="col-span-6 sm:col-span-6">
                  <label
                    htmlFor="parcelle"
                    className="block text-md font-bold leading-6 text-gray-900"
                  >
                    Choisir parcelle(s) *
                  </label>
                  <MultiSelect
                    className="my-3 border border-gray-300 text-gray-900 font-normal text-xl rounded-lg focus:ring-gexpertise-100 focus:border-gexpertise-200 w-full"
                    placeholder="Sélectionner vos parcelles"
                    value={selectedOptions}
                    options={queryClient.getQueryData(["parcelles"]) || []}
                    onChange={handleSelectChange}
                    optionLabel="label"
                    optionValue="value"
                    name="numéro"
                    display="chip"
                    maxSelectedLabels={5}
                    filter
                  />
                  {parcelleError && (
                    <span className="bg-red-100 text-red-800 text-md font-medium mr-2 mt-2 px-2.5 py-0.5 rounded">
                      champ obligatoire
                    </span>
                  )}
                </div>
              </div>

              {/* ── Structure du site ── */}
              <div className="border-gray-200 space-y-2 overflow-auto mt-5">
                <span className="text-lg font-semibold leading-6 text-gray-900">
                  Structure du site
                </span>

                {refSite?.batiments?.length === 0 ? (
                  <AlerteArborescenceSite />
                ) : (
                  <>
                    {refSite?.batiments?.map((batiment) => (
                      <React.Fragment key={batiment?.id}>
                        {/* Bâtiment */}
                        <div className="flex justify-between">
                          <div className="flex space-x-2">
                            <img src="/images/prochain.png" className="w-8 h-8" />
                            <button
                              type="button"
                              onClick={() => openModal("bâtiment", batiment)}
                              className="bg-white flex px-3 py-1 font-semibold text-md text-black border border-gray-400 rounded-md hover:bg-gray-200"
                            >
                              <div className="flex space-x-3">
                                <img src="/images/batiments-3d.png" className="w-6 h-6" />
                                <span>{batiment?.nom_bâtiment}</span>
                              </div>
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              setConfirmDelete({
                                type: "batiment",
                                id: batiment?.id,
                                label: batiment?.nom_bâtiment,
                              })
                            }
                            className="flex items-end px-4 py-2 bg-white-600 text-black text-sm font-medium rounded-md"
                          >
                            <img src="/images/effacer.png" />
                          </button>
                        </div>

                        {batiment?.niveaux?.map((niveau) => (
                          <React.Fragment key={niveau?.id}>
                            {/* Niveau */}
                            <div className="flex justify-between">
                              <div className="flex space-x-2 ml-10">
                                <img src="/images/prochain.png" className="w-8 h-8" />
                                <button
                                  type="button"
                                  onClick={() => openModal("niveau", niveau)}
                                  className="bg-white flex px-3 py-1 font-semibold text-md text-black border border-gray-400 rounded-md hover:bg-gray-200"
                                >
                                  <div className="flex space-x-3">
                                    <img src="/images/couches.png" className="w-6 h-6" />
                                    <span>{niveau.nom_niveau}</span>
                                  </div>
                                </button>
                              </div>
                              <button
                                type="button"
                                onClick={() =>
                                  setConfirmDelete({
                                    type: "niveau",
                                    id: niveau?.id,
                                    label: niveau?.nom_niveau,
                                  })
                                }
                                className="flex items-end px-4 py-2 bg-white-600 text-black text-sm font-medium rounded-md"
                              >
                                <img src="/images/effacer.png" />
                              </button>
                            </div>

                            {niveau.espaces?.map((espace) => (
                              <React.Fragment key={espace?.id}>
                                {/* Espace */}
                                <div className="flex justify-between">
                                  <div className="flex space-x-2 ml-20">
                                    <img src="/images/prochain.png" className="w-8 h-8" />
                                    <button
                                      type="button"
                                      onClick={() => openModal("espace", espace)}
                                      className="bg-white flex px-3 py-1 font-semibold text-md text-black border border-gray-400 rounded-md hover:bg-gray-200"
                                    >
                                      <div className="flex space-x-3">
                                        <img src="/images/espace.png" className="w-6 h-6" />
                                        <span>{espace?.nom_espace}</span>
                                      </div>
                                    </button>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setConfirmDelete({
                                        type: "espace",
                                        id: espace?.id,
                                        label: espace?.nom_espace,
                                      })
                                    }
                                    className="flex items-end px-4 py-2 bg-white-600 text-black text-sm font-medium rounded-md"
                                  >
                                    <img src="/images/effacer.png" />
                                  </button>
                                </div>

                                {espace.pieces?.map((piece) => (
                                  /* Pièce */
                                  <div className="flex justify-between" key={piece?.id}>
                                    <div className="flex space-x-2 ml-32">
                                      <img src="/images/prochain.png" className="w-8 h-8" />
                                      <button
                                        type="button"
                                        onClick={() => openModal("pièce", piece)}
                                        className="bg-white flex px-3 py-1 font-semibold text-md text-black border border-gray-400 rounded-md hover:bg-gray-200"
                                      >
                                        <div className="flex space-x-3">
                                          <img src="/images/piece.png" className="w-6 h-6" />
                                          <span>{piece?.nom_piece}</span>
                                        </div>
                                      </button>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setConfirmDelete({
                                          type: "piece",
                                          id: piece?.id,
                                          label: piece?.nom_piece,
                                        })
                                      }
                                      className="flex items-end px-4 py-2 bg-white-600 text-black text-sm font-medium rounded-md"
                                    >
                                      <img src="/images/effacer.png" />
                                    </button>
                                  </div>
                                ))}
                              </React.Fragment>
                            ))}
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    ))}
                  </>
                )}
              </div>

              <p className="text-lg font-thin my-4">* obligatoire</p>

              <div className="flex justify-end">
                <button
                  type="submit"
                  className="text-white mt-5 bg-gexpertise-200 hover:bg-gexpertise-100 font-medium rounded-lg text-md px-6 py-3 text-center"
                >
                  Modifier
                </button>
              </div>
            </form>
          </div>
        </div>
      </CustomDialog>

      {loading && <Loading />}

      {/* ── Dialog : édition d'une entité (bâtiment / niveau / espace / pièce) ── */}
      {selectedEntityType && (
        <CustomDialog
          visible={modals[selectedEntityType]}
          closeModalDialog={() => closeModal(selectedEntityType)}
        >
          <div className="relative w-full h-full">
            <EntityForm
              selectedEntityType={selectedEntityType}
              selectedEntity={selectedEntity}
              setSelectedEntity={setSelectedEntity}
              closeModal={closeModal}
              availableNiveaux={availableNiveaux}   // ← nouveau prop
            />
          </div>
        </CustomDialog>
      )}

      {/* ── Dialog : confirmation de suppression ── */}
      {confirmDelete && (
        <CustomDialog
          visible={!!confirmDelete}
          closeModalDialog={() => setConfirmDelete(null)}
          header="Confirmer la suppression"
        >
          <div className="p-6 space-y-4">
            <div className="flex items-start space-x-3">
              {/* Icône d'avertissement */}
              <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-red-100">
                <svg
                  className="w-5 h-5 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                  />
                </svg>
              </div>

              <div>
                <p className="text-md font-semibold text-gray-900">
                  Supprimer{" "}
                  {confirmDelete.type === "batiment" && "ce bâtiment"}
                  {confirmDelete.type === "niveau"   && "ce niveau"}
                  {confirmDelete.type === "espace"   && "cet espace"}
                  {confirmDelete.type === "piece"    && "cette pièce"}
                </p>
                <p className="mt-1 text-sm text-gray-600">
                  Voulez-vous vraiment supprimer le {deleteTypeLabels[confirmDelete.type]}{" "}
                  <span className="font-semibold text-gray-900">
                    « {confirmDelete.label} »
                  </span>{" "}
                  ? 
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Supprimer
              </button>
            </div>
          </div>
        </CustomDialog>
      )}
    </>
  );
};

export default ModalEditSite;